
import Vue from "vue";
import { Script } from "../../models/script";
import { ReportService } from "../../services/report-service";
import { IReportEngineSettingsWrapper } from "../../utils/report-engine-settings/report-engine-settings-wrapper";
import AppScriptArgInput from "@/components/scripts/ScriptArgInput.vue";
import AppReportAskedFilterInput from "./ReportAskedFilterInput.vue";
import { ReportType } from "@/utils/report-engine-settings/report-engine-settings";
import { IModel } from "@/utils/models/model-interface";
import { ScriptTableStructure } from "@/utils/report-engine-settings/script-table-structure";
import { BrowseService } from "@/services/browse-service";
import AppAskedTitlesInput from "./AskedTitlesInput.vue";
import AppMultiSelectBookYear from "@/components/bookyear/MultiSelectBookYear.vue";
import { BookYearService } from "@/services";
import { BrowseID } from "@/models/browse";

export default Vue.extend({
	data() {
		return {
			script: null as null | Script,
			isReady: false,
			scriptValues: [] as any[],
			selectedBookYears: [`${BookYearService.selectedBookYear}`] as string[],
			recreateBookkeeping: false
		};
	},
	props: {
		settings: {
			type: Object as () => IReportEngineSettingsWrapper,
			required: true
		},
		reportType: {
			type: String as () => ReportType,
			required: true
		}
	},
	computed: {
		askedTitlesCount():number {
			let result = 0;
			if (this.settings.Settings.AskTitle) {
				result++;
			}
			if (this.settings.Settings.AskSubTitle1) {
				result++;
			}
			if (this.settings.Settings.AskSubTitle2) {
				result++;
			}
			return result;
		},
		isBookkeepingReport():boolean {
			if (!this.settings.Settings.isSourceTypeBrowse()) return false;
			return this.settings.Settings.getBrowseIDFromDataSource() == BrowseID.BOOKKEEPING;
		},
		argsCount(): number {
			let result = this.settings.Settings.AskedFilters.length;
			if (this.script) {
				result += this.script.ExtraArgs.length;
			}
			result += this.askedTitlesCount;
			return result;
		},
		model():IModel | null {
			if (this.settings.Settings.isSourceTypeScript() || this.settings.Settings.isSourceTypeStandardScript()){
				if (!this.script) return null;
				return new ScriptTableStructure(JSON.parse(this.script.getValueOfStandardArg("structure")));
			}
			if (!this.settings.Settings.isSourceTypeBrowse()) return null;
			let browseId = this.settings.Settings.getBrowseIDFromDataSource();
			let browse = BrowseService.browses.find(b=>b.ID == browseId);
			if (!browse) return null;
			return browse.type;
		},
		bookyearsAreValid():boolean{
			if (!this.settings.Settings.isBookYearFilterRequired()) return true;
			if (this.useAllBookYears) return true;
			if (this.selectedBookYears.length > 0) return true;
			return false;
		},
		confirmDisabled():boolean{
			if (!this.bookyearsAreValid) {
				return true;
			}
			return false;
		},
		allBookYears():string[] {
			return BookYearService.bookYears.map(b=>`${b.Year}`).sort();
		},
		useAllBookYears():Boolean {
			if (this.selectedBookYears.length != this.allBookYears.length) return false;
			let sorted = [...this.selectedBookYears].sort();
			for (let i = 0; i < sorted.length; i++) {
				if (sorted[i] != this.allBookYears[i]) return false;
			}
			return true;
		},
	},
	watch: {
		script: {
			handler() {
				if (!this.script) return;
				this.scriptValues = this.script.ExtraArgs.map(s => {
					if (s.Type == "date") {
						if (s.DefaultValue) {
							return new Date(s.DefaultValue);
						}
						return new Date();
					}
					if (s.DefaultValue) {
						return s.DefaultValue;
					}
					if (s.Type == "number") {
						return 0;
					}
					return "";
				});
			}
		}
	},
	methods: {
		cancel() {
			this.$emit("close");
		},
		async submit() {
			let args = this.scriptValues.map((v, i) => {
				if (!this.script) return "";
				let t = this.script.ExtraArgs[i].Type;
				if (t == "date") {
					let date = v as Date;
					date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
					return date.toJSON();
				}
				return v.toString();
			});
			if (this.settings.Settings.isBookYearFilterRequired() && !this.useAllBookYears){
				this.settings.Settings.BookYears = this.selectedBookYears.map(f=>parseInt(f));
			}else{
				this.settings.Settings.BookYears = [];
			}
			this.settings.generate(this.reportType, args, {recreateBookkeeping: this.recreateBookkeeping});
			this.$emit("close");
		},
		async setUseAllBookYears(val:boolean){
			if (!val) {
				this.selectedBookYears.splice(0, this.selectedBookYears.length);
				this.selectedBookYears.push(`${BookYearService.selectedBookYear}`);
				return;
			}
			let message = this.$ct("are-you-sure-you-want-to-use-all-bookyears-this-can-be-slow");
			let confirm = await this.$confirmBuilder().setWarningTitle().setMessage(message).show();
			if (!confirm) {
				return;
			}
			this.selectedBookYears.splice(0, this.selectedBookYears.length);
			this.selectedBookYears.push(...this.allBookYears);
		}
	},
	async mounted() {
		if (this.settings.Settings.isSourceTypeScript() || this.settings.Settings.isSourceTypeStandardScript()) {
			this.script = await ReportService.getScript(this.settings.Settings.DataSource);
		}
		if (this.settings.Settings.BookYears.length != 0) {
			this.selectedBookYears = this.settings.Settings.BookYears.map(b=>`${b}`);
		}
		for (let filter of this.settings.Settings.AskedFilters) {
			if (filter.UseCurrentBookYear && filter.FieldType == "date" && filter.Values.length > 0) {
				let existingDate = new Date(filter.Values[0]);
				let oldBookYear = BookYearService.getBookYearFromDate(existingDate);
				let yearOffset = BookYearService.selectedBookYear - oldBookYear;
				for (let i = 0; i < filter.Values.length; i++) {
					let date = new Date(filter.Values[i]);
					date.setFullYear(date.getFullYear() + yearOffset);
					filter.Values[i] = date.toJSON();
				}
			}
		}
		this.isReady = true;

		if (this.argsCount == 0 && !this.settings.Settings.isBookYearFilterRequired() && !this.isBookkeepingReport) {
			this.submit();
		}
	},
	components: {
		AppScriptArgInput,
		AppReportAskedFilterInput,
		AppAskedTitlesInput,
		AppMultiSelectBookYear
	},
	i18nEx: {
		componentPrefix: "components.reportengine"
	}
});
